import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import type { CalendarEventAttachment } from "@/hooks/useCalendar";

/** Fetch all attachments for a calendar event. */
export function useCalendarEventAttachments(eventId: string | null) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ["calendar-event-attachments", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("calendar_event_attachment" as any)
        .select("*")
        .eq("event_id", eventId!)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data || []) as CalendarEventAttachment[];
    },
    enabled: !!eventId && isAuthenticated,
  });
}

/** Upload a file and create a calendar_event_attachment record. */
export function useUploadCalendarAttachment() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      eventId,
      orgId,
    }: {
      file: File;
      eventId: string;
      orgId: string;
    }) => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;

      // Upload to storage: intake-attachments bucket, calendar sub-folder
      const timestamp = Date.now();
      const storagePath = `${orgId}/calendar/${eventId}/${timestamp}_${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from("intake-attachments")
        .upload(storagePath, file, { cacheControl: "3600", upsert: false });

      if (uploadError) throw uploadError;

      // Resolve the uploader's user_account id
      let uploadedById: string | null = null;
      if (userId) {
        const { data: ua } = await supabase
          .from("user_account")
          .select("id, org_id")
          .eq("external_auth_user", userId)
          .single();
        uploadedById = ua?.id ?? null;
      }

      // Insert attachment record
      const { data, error: insertError } = await supabase
        .from("calendar_event_attachment" as any)
        .insert([{
          event_id: eventId,
          org_id: orgId,
          uploaded_by: uploadedById,
          filename: file.name,
          storage_path: storagePath,
          mime_type: file.type || null,
          file_size: file.size,
        }])
        .select()
        .single();

      if (insertError) throw insertError;
      return data as CalendarEventAttachment;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["calendar-event-attachments", vars.eventId],
      });
      toast({ title: "Attachment uploaded" });
    },
    onError: (error: Error) => {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/** Delete a calendar event attachment (removes DB record; storage cleanup is async). */
export function useDeleteCalendarAttachment() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      attachmentId,
      storagePath,
    }: {
      attachmentId: string;
      eventId: string;
      storagePath: string;
    }) => {
      // Remove from storage
      await supabase.storage
        .from("intake-attachments")
        .remove([storagePath]);

      // Remove DB record
      const { error } = await supabase
        .from("calendar_event_attachment" as any)
        .delete()
        .eq("id", attachmentId);

      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["calendar-event-attachments", vars.eventId],
      });
      toast({ title: "Attachment removed" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error removing attachment",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

/** Get a short-lived signed URL for a private attachment. */
export async function getAttachmentSignedUrl(storagePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from("intake-attachments")
    .createSignedUrl(storagePath, 3600);

  if (error || !data) return null;
  return data.signedUrl;
}
